// Anzeige-Helfer der E-Mail-Liste (Handoff E-Mails §7 „Liste“/„Zeile“). Reine UI-Schicht –
// welche Badges eine Mail bekommt, entscheidet weiter EmailListe; hier nur Farbton, Gruppierung, Datum.

import type { TonFarben } from '../ui/ton';

export type BadgeArt =
  | 'erledigt' | 'offen' | 'wiedervorlage' | 'rueckmeldung' | 'beantwortet' | 'geplant' | 'sendet' | 'fehlgeschlagen'
  | 'person' | 'team' | 'rechnung' | 'kipool' | 'duplikat' | 'abgelegt' | 'bearbeitung';

/** farbe: eigene Farbe statt der Art-Farbe (#527: Zuständige je Team/Mitarbeiter farbig). */
export interface ZeilenBadge { art: BadgeArt; label: string; farbe?: TonFarben }

export type ChipArt = 'mieter' | 'einheit' | 'eigentuemer' | 'objekt';

export interface ZuordnungsChip { art: ChipArt; label: string }

// Violett wie in Dokumente (lib/dokumente/ordner.ts) – keine neue Farbe.
const VIOLETT: TonFarben = { fg: '#8a63d2', bg: 'rgba(138,99,210,0.15)' };

export const BADGE_TON: Record<BadgeArt, TonFarben> = {
  erledigt: { fg: 'var(--ok)', bg: 'var(--ok-soft)' },
  offen: { fg: 'var(--warn)', bg: 'var(--warn-soft)' },
  wiedervorlage: { fg: 'var(--acc)', bg: 'var(--acc-soft)' },
  rueckmeldung: VIOLETT,
  beantwortet: { fg: 'var(--ok)', bg: 'var(--ok-soft)' },
  geplant: { fg: 'var(--info)', bg: 'var(--info-soft)' },
  sendet: { fg: 'var(--info)', bg: 'var(--info-soft)' },
  fehlgeschlagen: { fg: 'var(--bad)', bg: 'var(--bad-soft)' },
  person: { fg: 'var(--ink2)', bg: 'var(--fill2)' },
  team: { fg: 'var(--ink2)', bg: 'var(--fill2)' },
  rechnung: { fg: 'var(--ok)', bg: 'var(--ok-soft)' },
  kipool: VIOLETT,
  duplikat: { fg: 'var(--ink3)', bg: 'var(--fill2)' },
  abgelegt: { fg: 'var(--info)', bg: 'var(--info-soft)' },
  bearbeitung: { fg: 'var(--warn)', bg: 'var(--warn-soft)' },
};

export const CHIP_TON: Record<ChipArt, TonFarben> = {
  mieter: { fg: 'var(--info)', bg: 'var(--info-soft)' },
  einheit: { fg: 'var(--ink2)', bg: 'var(--fill2)' },
  eigentuemer: VIOLETT,
  objekt: { fg: 'var(--acc)', bg: 'var(--acc-soft)' },
};

export function initialen(s: string): string {
  // Nur Buchstaben/Ziffern zählen – „(Kein Empfänger)“ ergibt „KE“, nicht „(E“.
  const teile = (s || '?').trim().split(/[\s@._-]+/).map(t => t.replace(/[^\p{L}\p{N}]/gu, '')).filter(Boolean);
  if (teile.length === 0) return '?';
  if (teile.length === 1) return teile[0].slice(0, 2).toUpperCase();
  return (teile[0][0] + teile[teile.length - 1][0]).toUpperCase();
}

/** Gruppenkopf: „Heute · 14. Oktober 2026“, „Gestern · …“, sonst Wochentag + Datum. */
export function datumsGruppe(datum: string, jetzt: Date = new Date()): string {
  const d = new Date(datum);
  const startHeute = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate());
  const startGestern = new Date(startHeute); startGestern.setDate(startGestern.getDate() - 1);
  const datumStr = d.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
  if (d >= startHeute) return `Heute · ${datumStr}`;
  if (d >= startGestern) return `Gestern · ${datumStr}`;
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

export function relativesDatum(dateStr: string, jetzt: Date = new Date()): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const diffMs = jetzt.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'Gerade eben';
  if (diffMin < 60) return `vor ${diffMin}m`;
  if (diffH < 24) return `vor ${diffH}h`;
  if (diffD === 1) return 'Gestern';
  if (diffD < 7) return `vor ${diffD}d`;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function kuerze(text: string | null | undefined, max: number): string {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '...' : text;
}

/**
 * Konversations-Gruppierung: die neueste Mail je conversation_id wird Hauptzeile, ältere klappen darunter aus.
 * Entwürfe und „Konversationen aus“ bleiben ungruppiert. Erwartet die Liste bereits nach Datum absteigend.
 */
export function gruppiereNachKonversation<T extends { id: string; conversation_id?: string | null }>(
  liste: readonly T[], an: boolean,
): { email: T; rest: T[] }[] {
  if (!an) return liste.map(e => ({ email: e, rest: [] }));
  const proKonv = new Map<string, { email: T; rest: T[] }>();
  const reihenfolge: { email: T; rest: T[] }[] = [];
  for (const e of liste) {
    const key = e.conversation_id || `solo-${e.id}`;
    const g = proKonv.get(key);
    if (!g) { const neu = { email: e, rest: [] as T[] }; proKonv.set(key, neu); reihenfolge.push(neu); }
    else g.rest.push(e);
  }
  return reihenfolge;
}

/** Kontextmenü im Fenster halten (Menü ~240 × 380 px). */
export function menuePosition(x: number, y: number, fensterB: number, fensterH: number, breite = 250, hoehe = 380): { left: number; top: number } {
  return { left: Math.max(8, Math.min(x, fensterB - breite)), top: Math.max(8, Math.min(y, fensterH - hoehe)) };
}
